import { useEffect, useState, useRef } from "react";
import jsPDF from "jspdf";
import { QRCodeCanvas } from "qrcode.react";
import api, { formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { toast } from "sonner";
import { Plus, Printer, Trash2, FileText, X, Copy, Tag, Pencil, Search, ScrollText, GitBranch } from "lucide-react";
import { RecipeEditor } from "./RecipeEditor";

const ALLERGENS = ["Glutine", "Crostacei", "Uova", "Pesce", "Arachidi", "Soia", "Latte", "Frutta a guscio", "Sedano", "Senape", "Sesamo", "Solfiti", "Lupini", "Molluschi"];
const ALLERGEN_KEYWORDS = {
  Glutine: ["farina", "pane", "pasta", "pinsa", "pizza", "frumento", "grano", "orzo", "segale", "avena", "pangrattat", "couscous", "semola", "biscott", "frolla", "impasto"],
  Crostacei: ["gamber", "scampi", "astice", "granchio", "aragost", "crostace", "mazzancoll"],
  Uova: ["uovo", "uova", "maionese", "frittata", "albume", "tuorlo", "pasta all'uovo"],
  Pesce: ["pesce", "tonno", "salmone", "acciug", "alici", "baccal", "merluzzo", "branzino", "orata", "sgombro", "spada"],
  Arachidi: ["arachid", "nocciolin"],
  Soia: ["soia", "edamame", "tofu"],
  Latte: ["latte", "formagg", "mozzarell", "burro", "panna", "yogurt", "ricotta", "parmigian", "grana", "provol", "besciamell", "mascarpone", "stracchino", "gorgonzol", "scamorza", "pecorin", "asiago", "taleggio", "caciocavall", "caciotta", "fontina", "emmental", "cheddar", "brie", "camembert", "feta", "kefir", "siero", "ghee"],
  "Frutta a guscio": ["noci", "noce", "mandorl", "nocciol", "pistacch", "anacard", "pinoli", "pecan"],
  Sedano: ["sedano"],
  Senape: ["senape", "mostarda"],
  Sesamo: ["sesamo", "tahin"],
  Solfiti: ["vino", "aceto", "solfit", "frutta secca", "funghi secchi", "mosto"],
  Lupini: ["lupini"],
  Molluschi: ["vongole", "cozze", "calamar", "seppi", "polpo", "totani", "mollusch", "ostrich", "moscardin"],
};
const CONSERV = { frigo: "Frigorifero (0–4°C)", ambiente: "Temperatura ambiente", congelatore: "Congelatore (−18°C)", abbattitore: "Abbattuto e conservato" };
const CONSERV_TEMP = { frigo: "0–4°C", ambiente: "", congelatore: "−18°C", abbattitore: "−18°C" };
const CONSERV_SHORT = { frigo: "Frigo", ambiente: "Ambiente", congelatore: "Congelatore", abbattitore: "Abbattuto" };
const LABEL_PRESETS = { "5x3": { w: 50, h: 30 }, "7x4": { w: 70, h: 40 }, "10x5": { w: 100, h: 50 } };
const today = () => new Date().toISOString().slice(0, 10);
const emptyIng = () => ({ name: "", supplier: "", lotto: "", expiry: "", quantity: "", unit: "pz" });
const emptySheet = () => ({ name: "", prep_date: today(), prep_time: "", conservation: "frigo", conservation_temp: "0–4°C", expiry: "", blast_start: "", blast_end: "", procedure: "", operator: "", notes: "", allergens: [], ingredients: [emptyIng()] });

function flattenTraceSheet(nodes, depth, acc) {
  acc = acc || [];
  (nodes || []).forEach((n) => {
    acc.push({ node: n, depth });
    if (n.type === "internal" && n.children && n.children.length) flattenTraceSheet(n.children, depth + 1, acc);
  });
  return acc;
}

export function ProductSheets({ canManage }) {
  const [sheets, setSheets] = useState([]);
  const [batches, setBatches] = useState([]);
  const [internalLots, setInternalLots] = useState([]);
  const [traceItem, setTraceItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptySheet());
  const [labelOpen, setLabelOpen] = useState(false);
  const [labelSheet, setLabelSheet] = useState(null);
  const [preset, setPreset] = useState("5x3");
  const [dim, setDim] = useState({ w: 50, h: 30 });
  const [editId, setEditId] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const qrRef = useRef(null);
  const [askRecipeSheet, setAskRecipeSheet] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorInitial, setEditorInitial] = useState(null);

  const buildRecipeFromSheet = async (sheet) => {
    setAskRecipeSheet(null);
    try {
      const { data } = await api.post("/recipes/build-from-sheet", { sheet_id: sheet.id });
      setEditorInitial(data.payload);
      setEditorOpen(true);
      if (data.existing && data.existing.length) toast.message("Esiste già una ricetta per questo prodotto: al salvataggio potrai aprirla o creare una nuova versione.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const suggestAllergens = () => {
    const text = form.ingredients.map((i) => (i.name || "")).join(" ").toLowerCase();
    if (!text.trim()) return toast.message("Inserisci prima gli ingredienti");
    const found = new Set(form.allergens);
    Object.entries(ALLERGEN_KEYWORDS).forEach(([al, kws]) => { if (kws.some((k) => text.includes(k))) found.add(al); });
    const arr = ALLERGENS.filter((a) => found.has(a));
    const added = arr.length - form.allergens.length;
    setForm((f) => ({ ...f, allergens: arr }));
    toast.success(added > 0 ? `Rilevati ${added} nuovi allergeni dai prodotti` : "Nessun nuovo allergene rilevato");
  };

  const load = async () => {
    const [s, b, p] = await Promise.all([
      api.get("/product-sheets"),
      api.get("/batches").catch(() => ({ data: [] })),
      api.get("/productions").catch(() => ({ data: [] })),
    ]);
    setSheets(s.data); setBatches(b.data);
    // LOTTI INTERNI (semilavorati): solo confermati, con residuo > 0, non scaduti, con semi_ingredient_id
    const now = Date.now();
    const lots = (p.data || []).filter((x) =>
      x.status === "confirmed" && x.semi_ingredient_id && Number(x.quantity_remaining_produced) > 0 &&
      !(x.internal_expiry && new Date(x.internal_expiry).getTime() < now)
    );
    setInternalLots(lots);
  };
  useEffect(() => { load(); }, []);

  const setIng = (i, k, v) => setForm((f) => ({ ...f, ingredients: f.ingredients.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addIng = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, emptyIng()] }));
  const delIng = (i) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, idx) => idx !== i) }));
  const addFromBatch = (bid) => {
    const b = batches.find((x) => x.id === bid);
    if (!b) return;
    setForm((f) => ({ ...f, ingredients: [...f.ingredients.filter((it) => it.name || it.lotto), { name: b.product || "", supplier: b.supplier || "", lotto: b.lotto || "", expiry: b.expiry || "", quantity: "", unit: b.unit || "pz", source_type: "supplier_batch" }] }));
  };
  const addFromInternal = (pid) => {
    const l = internalLots.find((x) => x.id === pid);
    if (!l) return;
    const exp = l.internal_expiry ? String(l.internal_expiry).slice(0, 10) : "";
    setForm((f) => ({ ...f, ingredients: [...f.ingredients.filter((it) => it.name || it.lotto), {
      name: l.recipe_name || "", supplier: "Semilavorato interno", lotto: l.internal_lot || "", expiry: exp,
      quantity: "", unit: l.semi_unit || l.yield_unit || "pz",
      source_type: "internal_production", input_production_id: l.production_id, input_internal_lot: l.internal_lot,
      semi_ingredient_id: l.semi_ingredient_id, internal_expiry: l.internal_expiry || "", production_date: l.production_date || (l.produced_at ? String(l.produced_at).slice(0, 10) : ""),
    }] }));
  };
  const addFromSheet = (sid) => {
    const sh = sheets.find((x) => x.id === sid);
    if (!sh) return;
    setForm((f) => ({ ...f, ingredients: [...f.ingredients.filter((it) => it.name || it.lotto), {
      name: sh.name || "", supplier: "Semilavorato interno", lotto: sh.lot_number || "", expiry: sh.expiry || "",
      quantity: "", unit: "pz",
      source_type: "product_sheet", input_sheet_id: sh.id, input_internal_lot: sh.lot_number,
      internal_expiry: sh.expiry || "", production_date: sh.prep_date || "",
    }] }));
  };
  const openTrace = async (it) => {
    if (it.source_type === "product_sheet") {
      const origin = sheets.find((s) => s.id === it.input_sheet_id || s.lot_number === it.input_internal_lot);
      setTraceItem({ kind: "sheet", name: it.name, lot: it.input_internal_lot, ingredients: origin?.ingredients || [] });
    } else if (it.source_type === "internal_production") {
      const prod = internalLots.find((l) => l.production_id === it.input_production_id || l.internal_lot === it.input_internal_lot);
      let tree = null;
      if (prod) { try { const { data } = await api.get(`/productions/${prod.id}/trace-tree`); tree = data; } catch { /* noop */ } }
      setTraceItem({ kind: "production", name: it.name, lot: it.input_internal_lot, tree });
    }
  };
  const toggleAllergen = (a) => setForm((f) => ({ ...f, allergens: f.allergens.includes(a) ? f.allergens.filter((x) => x !== a) : [...f.allergens, a] }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Inserisci la denominazione del prodotto");
    try {
      const payload = { ...form, ingredients: form.ingredients.filter((it) => it.name).map((it) => ({ ...it, quantity: parseFloat(it.quantity) || 0 })) };
      if (editId) {
        await api.put(`/product-sheets/${editId}`, payload);
        toast.success("Scheda aggiornata"); setOpen(false); setEditId(null); load();
      } else {
        const { data } = await api.post("/product-sheets", payload);
        toast.success("Scheda prodotto creata"); setOpen(false); load();
        setAskRecipeSheet(data);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openEdit = (s) => {
    setEditId(s.id);
    setForm({
      name: s.name || "", prep_date: s.prep_date || today(), prep_time: s.prep_time || "",
      conservation: s.conservation || "frigo", conservation_temp: s.conservation_temp || CONSERV_TEMP[s.conservation] || "",
      expiry: s.expiry || "", blast_start: s.blast_start || "", blast_end: s.blast_end || "",
      procedure: s.procedure || "", operator: s.operator || "", notes: s.notes || "",
      allergens: [...(s.allergens || [])],
      ingredients: (s.ingredients && s.ingredients.length) ? s.ingredients.map((it) => ({ ...it, quantity: it.quantity || "" })) : [emptyIng()],
    });
    setOpen(true);
  };
  const remove = async (id) => { if (!window.confirm("Eliminare questa scheda prodotto?")) return; await api.delete(`/product-sheets/${id}`); load(); };

  const duplicate = (s) => {
    setForm({
      name: s.name || "", prep_date: today(), prep_time: "",
      conservation: s.conservation || "frigo", conservation_temp: s.conservation_temp || CONSERV_TEMP[s.conservation] || "",
      expiry: "", blast_start: "", blast_end: "", procedure: s.procedure || "",
      operator: s.operator || "", notes: s.notes || "", allergens: [...(s.allergens || [])],
      ingredients: (s.ingredients && s.ingredients.length) ? s.ingredients.map((it) => ({ ...it, quantity: it.quantity || "" })) : [emptyIng()],
    });
    setOpen(true);
    toast.message("Scheda duplicata: controlla la data e salva");
  };

  const openLabel = (s) => { setLabelSheet(s); setPreset("5x3"); setDim({ w: 50, h: 30 }); setLabelOpen(true); };
  const setPresetSize = (p) => { setPreset(p); if (p !== "custom") setDim(LABEL_PRESETS[p]); };
  const generateLabel = async () => {
    const s = labelSheet;
    const w = Math.max(30, parseFloat(dim.w) || 50);
    const h = Math.max(18, parseFloat(dim.h) || 30);
    let biz = { name: "Dogana 2.0" };
    try { biz = (await api.get("/settings/business")).data; } catch { /* noop */ }
    const doc = new jsPDF({ unit: "mm", format: [w, h], orientation: w >= h ? "landscape" : "portrait" });
    const pad = 2.5;
    const qs = Math.min(h - 2 * pad, 16);          // QR ~16mm max
    const qrX = w - qs - pad;
    const qrY = pad;
    const leftW = qrX - pad - 1.5;                 // larghezza utile colonna testo (non invade il QR)
    const valX = pad + 13;                         // offset etichetta -> valore
    doc.setDrawColor(160); doc.roundedRect(1, 1, w - 2, h - 2, 1.5, 1.5);
    const qrCanvas = qrRef.current?.querySelector("canvas");
    if (qrCanvas) { try { doc.addImage(qrCanvas.toDataURL("image/png"), "PNG", qrX, qrY, qs, qs); } catch { /* noop */ } }
    doc.setFontSize(5.5); doc.setTextColor(120); doc.text((biz.name || "Dogana 2.0").slice(0, 30), pad, pad + 2);
    doc.setTextColor(20); doc.setFont("helvetica", "bold"); doc.setFontSize(8);
    doc.text(doc.splitTextToSize(s.name || "Prodotto", leftW).slice(0, 2), pad, pad + 6);
    doc.setFont("helvetica", "normal"); doc.setFontSize(6.5);
    let y = pad + 12;
    const line = (label, val) => {
      if (y > h - pad) return;
      doc.setFont("helvetica", "bold"); doc.text(label, pad, y);
      doc.setFont("helvetica", "normal");
      doc.text((doc.splitTextToSize(String(val || "—"), leftW - 13)[0] || "—"), valX, y);
      y += 4;
    };
    line("Lotto:", s.lot_number);
    line("Prodotto il:", `${s.prep_date || "—"}${s.prep_time ? " " + s.prep_time : ""}`);
    line("Scadenza:", s.expiry || "—");
    line("Conserv.:", `${CONSERV_SHORT[s.conservation] || s.conservation}${s.conservation_temp ? " " + s.conservation_temp : ""}`);
    if (s.allergens && s.allergens.length && y <= h - pad) { doc.setFont("helvetica", "bold"); doc.text("Allergeni:", pad, y); doc.setFont("helvetica", "normal"); doc.text(doc.splitTextToSize(s.allergens.join(", "), leftW - 13).slice(0, 1), valX, y); }
    doc.save(`etichetta-${(s.name || "prodotto").replace(/\s+/g, "-").toLowerCase()}.pdf`);
    toast.success("Etichetta generata"); setLabelOpen(false);
  };

  const generatePdf = async (s) => {
    let biz = { name: "Dogana 2.0", address: "", vat: "", phone: "" };
    try { biz = (await api.get("/settings/business")).data; } catch { /* noop */ }
    const logo = await new Promise((res) => { fetch("/logo-dogana.png").then((r) => r.blob()).then((b) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => res(null); fr.readAsDataURL(b); }).catch(() => res(null)); });
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    if (logo) { try { doc.addImage(logo, "PNG", 14, 10, 18, 18); } catch { /* noop */ } }
    const hx = logo ? 36 : 14;
    doc.setFontSize(13); doc.setFont("helvetica", "bold"); doc.text(biz.name || "Dogana 2.0", hx, 16);
    doc.setFontSize(8); doc.setFont("helvetica", "normal"); let hy = 21;
    if (biz.address) { doc.text(biz.address, hx, hy); hy += 4; }
    const l2 = [biz.vat && `P.IVA: ${biz.vat}`, biz.phone && `Tel: ${biz.phone}`].filter(Boolean).join("    ");
    if (l2) doc.text(l2, hx, hy);
    doc.setDrawColor(180); doc.line(14, 30, 196, 30);
    doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.setTextColor(194, 65, 39);
    doc.text("SCHEDA DI RINTRACCIABILITÀ PRODOTTO", 105, 38, { align: "center" });
    doc.setTextColor(26);
    let y = 47;
    const row = (label, val) => { doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.text(label, 14, y); doc.setFont("helvetica", "normal"); doc.text(String(val || "—"), 60, y); y += 6; };
    row("Denominazione prodotto:", s.name);
    row("Lotto interno:", s.lot_number);
    row("Data / ora produzione:", `${s.prep_date || "—"}${s.prep_time ? " " + s.prep_time : ""}`);
    row("Conservazione:", `${CONSERV[s.conservation] || s.conservation}${s.conservation_temp ? " (" + s.conservation_temp + ")" : ""}`);
    if (s.blast_start || s.blast_end) row("Abbattimento (inizio–fine):", `${s.blast_start || "—"} – ${s.blast_end || "—"}`);
    row("Scadenza / consumare entro:", s.expiry);
    row("Responsabile:", s.operator);
    y += 2;
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.text("Ingredienti / lotti utilizzati", 14, y); y += 2;
    doc.setDrawColor(210); doc.line(14, y, 196, y); y += 5;
    doc.setFontSize(8); doc.setFont("helvetica", "bold");
    const xs = [14, 74, 108, 140, 176];
    ["Ingrediente", "Fornitore", "Lotto", "Scadenza", "Q.tà"].forEach((h, i) => doc.text(h, xs[i], y));
    y += 2; doc.line(14, y, 196, y); y += 4; doc.setFont("helvetica", "normal");
    (s.ingredients || []).forEach((it) => {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.text(String(it.name || "—").slice(0, 32), xs[0], y);
      doc.text(String(it.supplier || "—").slice(0, 18), xs[1], y);
      doc.text(String(it.lotto || "—").slice(0, 16), xs[2], y);
      doc.text(String(it.expiry || "—").slice(0, 12), xs[3], y);
      doc.text(`${it.quantity || 0} ${it.unit || ""}`, xs[4], y);
      y += 5;
    });
    if (!(s.ingredients || []).length) { doc.text("Nessun ingrediente registrato.", 14, y); y += 5; }
    y += 3;
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.text("Allergeni presenti:", 14, y);
    doc.setFont("helvetica", "normal");
    const allTxt = (s.allergens && s.allergens.length) ? s.allergens.join(", ") : "Nessuno dichiarato";
    doc.text(doc.splitTextToSize(allTxt, 130), 60, y); y += 8;
    if (s.procedure) {
      doc.setFont("helvetica", "bold"); doc.text("Procedura di preparazione:", 14, y); y += 5;
      doc.setFont("helvetica", "normal");
      doc.splitTextToSize(s.procedure, 182).forEach((ln) => { if (y > 275) { doc.addPage(); y = 20; } doc.text(ln, 14, y); y += 4.5; });
      y += 3;
    }
    if (s.notes) { doc.setFont("helvetica", "bold"); doc.text("Note:", 14, y); doc.setFont("helvetica", "normal"); doc.text(doc.splitTextToSize(s.notes, 170), 28, y); y += 8; }
    doc.setFontSize(7); doc.setTextColor(120);
    doc.text("Documento conforme a Reg. CE 178/2002, 852/2004 e Reg. UE 1169/2011 (allergeni). Da esibire in fase di controllo ASL/NAS.", 14, 288);
    doc.save(`scheda-${(s.name || "prodotto").replace(/\s+/g, "-").toLowerCase()}.pdf`);
    toast.success("PDF scheda generato");
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">Schede di preparazione interne (salse, sughi, semilavorati, piatti pronti) con lotti, conservazione e allergeni — pronte per i controlli NAS/ASL.</p>
        {canManage && <Button onClick={() => { setEditId(null); setForm(emptySheet()); setOpen(true); }} className="rounded-full shrink-0" data-testid="add-sheet-button"><Plus className="w-4 h-4 mr-1" /> Nuova scheda prodotto</Button>}
      </div>

      <Card className="rounded-xl overflow-hidden">
        <Table data-testid="sheets-table">
          <TableHeader><TableRow><TableHead>Prodotto</TableHead><TableHead>Lotto interno</TableHead><TableHead>Prodotto il</TableHead><TableHead>Conservazione</TableHead><TableHead>Scadenza</TableHead><TableHead>Allergeni</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
          <TableBody>
            {sheets.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nessuna scheda prodotto. Creane una con "Nuova scheda prodotto".</TableCell></TableRow>}
            {sheets.map((s) => (
              <TableRow key={s.id} data-testid={`sheet-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-xs">{s.lot_number}</TableCell>
                <TableCell>{s.prep_date}</TableCell>
                <TableCell className="text-sm">{CONSERV[s.conservation] || s.conservation}</TableCell>
                <TableCell>{s.expiry || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{(s.allergens || []).length ? `${s.allergens.length} dichiarati` : "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => openLabel(s)} data-testid={`label-sheet-${s.id}`} title="Etichetta"><Tag className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => generatePdf(s)} data-testid={`pdf-sheet-${s.id}`} title="Stampa PDF"><Printer className="w-4 h-4 text-primary" /></Button>
                  {canManage && <Button size="icon" variant="ghost" onClick={() => buildRecipeFromSheet(s)} data-testid={`sheet-to-recipe-${s.id}`} title="Crea ricetta"><ScrollText className="w-4 h-4 text-primary" /></Button>}
                  {canManage && <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-sheet-${s.id}`} title="Modifica"><Pencil className="w-4 h-4 text-primary" /></Button>}
                  {canManage && <Button size="icon" variant="ghost" onClick={() => duplicate(s)} data-testid={`duplicate-sheet-${s.id}`} title="Duplica"><Copy className="w-4 h-4" /></Button>}
                  {canManage && <Button size="icon" variant="ghost" onClick={() => remove(s.id)} data-testid={`delete-sheet-${s.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditId(null); }}>
        <DialogContent className="max-w-2xl" data-testid="sheet-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /> {editId ? "Modifica scheda prodotto" : "Nuova scheda prodotto"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Denominazione prodotto / preparazione *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Ragù di carne, Salsa tonnata…" data-testid="sheet-name-input" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Data produzione</Label><Input type="date" value={form.prep_date} onChange={(e) => setForm({ ...form, prep_date: e.target.value })} data-testid="sheet-date-input" /></div>
              <div className="space-y-2"><Label>Ora</Label><Input type="time" value={form.prep_time} onChange={(e) => setForm({ ...form, prep_time: e.target.value })} data-testid="sheet-time-input" /></div>
              <div className="space-y-2"><Label>Consumare entro</Label><Input type="date" value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} data-testid="sheet-expiry-input" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Conservazione</Label>
                <Select value={form.conservation} onValueChange={(v) => setForm({ ...form, conservation: v, conservation_temp: CONSERV_TEMP[v] })}>
                  <SelectTrigger data-testid="sheet-conservation-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CONSERV).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Temperatura</Label><Input value={form.conservation_temp} onChange={(e) => setForm({ ...form, conservation_temp: e.target.value })} placeholder="es. 0–4°C" data-testid="sheet-temp-input" /></div>
            </div>
            {(form.conservation === "abbattitore") && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Abbattimento inizio</Label><Input type="time" value={form.blast_start} onChange={(e) => setForm({ ...form, blast_start: e.target.value })} data-testid="sheet-blaststart-input" /></div>
                <div className="space-y-2"><Label>Abbattimento fine</Label><Input type="time" value={form.blast_end} onChange={(e) => setForm({ ...form, blast_end: e.target.value })} data-testid="sheet-blastend-input" /></div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label>Ingredienti / lotti utilizzati</Label>
                <div className="flex items-center gap-2">
                  {(batches.length > 0 || internalLots.length > 0 || sheets.filter((s) => s.id !== editId).length > 0) && (
                    <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                      <PopoverTrigger asChild>
                        <Button type="button" size="sm" variant="outline" className="rounded-full h-8 text-xs" data-testid="sheet-add-from-batch"><Search className="w-3.5 h-3.5 mr-1" /> Cerca ingrediente</Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[360px]" align="end">
                        <Command>
                          <CommandInput placeholder="Cerca lotti fornitore o semilavorati interni…" data-testid="sheet-ing-search" />
                          <CommandList>
                            <CommandEmpty>Nessun lotto trovato</CommandEmpty>
                            {(internalLots.length > 0 || sheets.filter((s) => s.id !== editId).length > 0) && (
                              <CommandGroup heading="Semilavorato interno">
                                {internalLots.slice(0, 500).map((l) => (
                                  <CommandItem key={`prod-${l.id}`} value={`${l.recipe_name} ${l.internal_lot || ""} semilavorato produzione`} onSelect={() => { addFromInternal(l.id); setSearchOpen(false); }} data-testid={`sheet-internal-opt-prod-${l.id}`}>
                                    <div className="flex flex-col">
                                      <span className="text-sm flex items-center gap-1.5"><span className="px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-600 text-[10px] font-semibold">PRODUZIONE</span>{l.recipe_name}</span>
                                      <span className="text-xs text-muted-foreground">Lotto {l.internal_lot || "—"} · Disp. {l.quantity_remaining_produced} {l.semi_unit || l.yield_unit || ""} · Scad. {l.internal_expiry ? new Date(l.internal_expiry).toLocaleDateString("it-IT") : "DA DEFINIRE"}</span>
                                    </div>
                                  </CommandItem>
                                ))}
                                {sheets.filter((s) => s.id !== editId).slice(0, 500).map((sh) => (
                                  <CommandItem key={`sheet-${sh.id}`} value={`${sh.name} ${sh.lot_number || ""} semilavorato scheda haccp`} onSelect={() => { addFromSheet(sh.id); setSearchOpen(false); }} data-testid={`sheet-internal-opt-sheet-${sh.id}`}>
                                    <div className="flex flex-col">
                                      <span className="text-sm flex items-center gap-1.5"><span className="px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-600 text-[10px] font-semibold">SCHEDA HACCP</span>{sh.name}</span>
                                      <span className="text-xs text-muted-foreground">Lotto {sh.lot_number || "—"} · Prodotta il {sh.prep_date || "—"} · Scad. {sh.expiry || "DA DEFINIRE"}</span>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                            {batches.length > 0 && (
                              <CommandGroup heading="Lotto fornitore">
                                {batches.slice(0, 500).map((b) => (
                                  <CommandItem key={b.id} value={`${b.product} ${b.lotto || ""} ${b.supplier || ""}`} onSelect={() => { addFromBatch(b.id); setSearchOpen(false); }} data-testid={`sheet-batch-opt-${b.id}`}>
                                    <div className="flex flex-col"><span className="text-sm flex items-center gap-1.5"><span className="px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 text-[10px] font-semibold">FORNITORE</span>{b.product}</span><span className="text-xs text-muted-foreground">L.{b.lotto || "—"} · {b.supplier || ""} · scad. {b.expiry || "—"}</span></div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                  <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={addIng} data-testid="sheet-add-ingredient">+ Riga</Button>
                </div>
              </div>
              <div className="space-y-3" data-testid="sheet-ingredients">
                {form.ingredients.map((it, i) => (
                  <div key={i} data-testid={`sheet-ing-${i}`} className="rounded-xl border border-border p-3 grid grid-cols-2 sm:grid-cols-12 gap-2 items-end">
                    <div className="col-span-2 sm:col-span-4 space-y-1"><Label className="text-xs text-muted-foreground">Ingrediente</Label><Input value={it.name} onChange={(e) => setIng(i, "name", e.target.value)} className="h-9" data-testid={`sheet-ing-name-${i}`} /></div>
                    <div className="col-span-1 sm:col-span-2 space-y-1"><Label className="text-xs text-muted-foreground">Fornitore</Label><Input value={it.supplier} onChange={(e) => setIng(i, "supplier", e.target.value)} className="h-9" data-testid={`sheet-ing-supplier-${i}`} /></div>
                    <div className="col-span-1 sm:col-span-2 space-y-1"><Label className="text-xs text-muted-foreground">Lotto</Label><Input value={it.lotto} onChange={(e) => setIng(i, "lotto", e.target.value)} className="h-9" data-testid={`sheet-ing-lotto-${i}`} /></div>
                    <div className="col-span-1 sm:col-span-2 space-y-1"><Label className="text-xs text-muted-foreground">Scadenza</Label><Input type="date" value={it.expiry} onChange={(e) => setIng(i, "expiry", e.target.value)} className="h-9" data-testid={`sheet-ing-expiry-${i}`} /></div>
                    <div className="col-span-1 sm:col-span-2 space-y-1"><Label className="text-xs text-muted-foreground">Q.tà</Label>
                      <div className="flex items-center gap-1">
                        <Input type="number" step="0.01" inputMode="decimal" value={it.quantity} onChange={(e) => setIng(i, "quantity", e.target.value)} className="h-9" data-testid={`sheet-ing-qty-${i}`} />
                        {it.source_type && <button type="button" onClick={() => openTrace(it)} data-testid={`sheet-ing-trace-${i}`} className="shrink-0 text-primary p-1" title="Tracciabilità"><GitBranch className="w-4 h-4" /></button>}
                        <button type="button" onClick={() => delIng(i)} data-testid={`sheet-ing-del-${i}`} className="shrink-0 text-destructive p-1" title="Rimuovi"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label>Allergeni presenti (Reg. UE 1169/2011)</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={suggestAllergens} data-testid="suggest-allergens-button">Suggerisci dai prodotti</Button>
            </div>
              <div className="flex flex-wrap gap-2">
                {ALLERGENS.map((a) => (
                  <button key={a} type="button" onClick={() => toggleAllergen(a)} data-testid={`sheet-allergen-${a}`}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${form.allergens.includes(a) ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-transparent hover:border-primary/40"}`}>
                    {a}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2"><Label>Procedura di preparazione</Label>
              <textarea value={form.procedure} onChange={(e) => setForm({ ...form, procedure: e.target.value })} rows={4} placeholder="Descrivi la procedura: fasi, temperature di cottura, tempi…" data-testid="sheet-procedure-input"
                className="flex w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Responsabile</Label><Input value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} placeholder="Lascia vuoto = tuo nome" data-testid="sheet-operator-input" /></div>
              <div className="space-y-2"><Label>Note</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="sheet-notes-input" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="save-sheet-button">Salva scheda</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={labelOpen} onOpenChange={setLabelOpen}>
        <DialogContent data-testid="label-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Tag className="w-5 h-5 text-primary" /> Etichetta · {labelSheet?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Formato etichetta</Label>
              <Select value={preset} onValueChange={setPresetSize}>
                <SelectTrigger data-testid="label-preset-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="5x3">5 × 3 cm</SelectItem>
                  <SelectItem value="7x4">7 × 4 cm</SelectItem>
                  <SelectItem value="10x5">10 × 5 cm</SelectItem>
                  <SelectItem value="custom">Personalizzato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {preset === "custom" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Larghezza (mm)</Label><Input type="number" value={dim.w} onChange={(e) => setDim({ ...dim, w: e.target.value })} data-testid="label-width-input" /></div>
                <div className="space-y-2"><Label>Altezza (mm)</Label><Input type="number" value={dim.h} onChange={(e) => setDim({ ...dim, h: e.target.value })} data-testid="label-height-input" /></div>
              </div>
            )}
            <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
              L'etichetta includerà: nome prodotto, lotto interno, data di produzione, scadenza, modalità di conservazione, allergeni e un <b>QR code</b> che apre la scheda di rintracciabilità completa (utile durante i controlli).
            </div>
            {labelSheet && <div ref={qrRef} className="hidden"><QRCodeCanvas value={`${window.location.origin}/scheda/${labelSheet.id}`} size={240} level="M" /></div>}
          </div>
          <DialogFooter><Button onClick={generateLabel} className="rounded-full" data-testid="generate-label-button"><Printer className="w-4 h-4 mr-1" /> Genera etichetta PDF</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!askRecipeSheet} onOpenChange={(o) => { if (!o) setAskRecipeSheet(null); }}>
        <DialogContent className="max-w-md" data-testid="sheet-to-recipe-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScrollText className="w-5 h-5 text-primary" /> Vuoi trasformare questa scheda in una ricetta?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">La scheda <b>{askRecipeSheet?.name}</b> è stata salvata. Puoi crearne subito la ricetta (come si produce normalmente il prodotto), oppure farlo più tardi dal pulsante dedicato.</p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button className="rounded-full" onClick={() => buildRecipeFromSheet(askRecipeSheet)} data-testid="sheet-recipe-yes">Sì, crea ricetta</Button>
            <Button variant="outline" className="rounded-full" onClick={() => setAskRecipeSheet(null)} data-testid="sheet-recipe-no">No, solo scheda</Button>
            <Button variant="ghost" className="rounded-full" onClick={() => setAskRecipeSheet(null)} data-testid="sheet-recipe-later">Più tardi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!traceItem} onOpenChange={(o) => { if (!o) setTraceItem(null); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="doc-trace-dialog">
          {traceItem && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><GitBranch className="w-5 h-5 text-primary" /> Tracciabilità · {traceItem.name}</DialogTitle></DialogHeader>
              <div className="text-sm space-y-1">
                <div className="text-xs text-muted-foreground mb-1">{traceItem.kind === "sheet" ? "Scheda HACCP" : "Produzione"} · Lotto {traceItem.lot || "—"}</div>
                {traceItem.kind === "sheet" && (traceItem.ingredients.length ? traceItem.ingredients.map((g, idx) => (
                  <div key={idx} className="border-l-2 border-border pl-2.5 text-xs" data-testid={`doc-trace-ing-${idx}`}>
                    <b>{g.name}</b> <span className="text-muted-foreground">{g.supplier ? `· ${g.supplier} ` : ""}· lotto {g.lotto || "—"}{g.expiry ? ` · scad ${g.expiry}` : ""}{g.quantity ? ` · ${g.quantity} ${g.unit || ""}` : ""}</span>
                  </div>
                )) : <p className="text-xs text-muted-foreground">Nessun ingrediente registrato nella scheda origine.</p>)}
                {traceItem.kind === "production" && (traceItem.tree ? flattenTraceSheet(traceItem.tree.children, 0).map((row, idx) => {
                  const n = row.node;
                  return (
                    <div key={idx} className="border-l-2 border-border text-xs" style={{ marginLeft: row.depth * 14, paddingLeft: 10 }} data-testid={`doc-trace-node-${idx}`}>
                      <span className="flex items-center gap-1.5 flex-wrap py-0.5">
                        {n.type === "internal" ? <span className="px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-600 text-[10px] font-semibold">semilavorato</span> : <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 text-[10px] font-semibold">fornitore</span>}
                        <b>{n.name}</b>
                        <span className="text-muted-foreground">{n.type === "internal" ? n.internal_lot : `${n.supplier ? n.supplier + " · " : ""}lotto ${n.lotto || "—"}`} · {n.quantity_used} {n.unit}</span>
                      </span>
                    </div>
                  );
                }) : <p className="text-xs text-muted-foreground">Tracciabilità non disponibile.</p>)}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {editorOpen && <RecipeEditor open={editorOpen} onOpenChange={setEditorOpen} initial={editorInitial} context={{ mode: "new" }} onSaved={() => { setEditorOpen(false); toast.success("Ricetta salvata"); }} />}
    </div>
  );
}
